import { MessageSquare, ExternalLink, Mail, Copy, Check, UserPlus } from 'lucide-react';
import { inferAgencyFromInfluencer, computeRecruitPriority } from '../../utils/scootUtils';
import { useState } from 'react';

interface ScootResultCardProps {
  creator: any;
  onGenerateMessage: () => void;
  onAddToRecruitment?: () => void;
  isRecruited?: boolean;
  recruitmentStatus?: string;
}

export default function ScootResultCard({ creator, onGenerateMessage, onAddToRecruitment, isRecruited = false, recruitmentStatus }: ScootResultCardProps) {
  const [emailCopied, setEmailCopied] = useState(false);

  const copyEmail = async () => {
    const emailToCopy = creator.email || extractEmailFromBio(creator.bio);
    if (emailToCopy) {
      await navigator.clipboard.writeText(emailToCopy);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    }
  };

  const formatNumber = (num: number) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString('fr-FR');
  };

  const getEngagementColor = (rate: number) => {
    if (rate > 3) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (rate >= 1) return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
    return 'text-red-400 bg-red-500/10 border-red-500/20';
  };

  const engagementRate = creator.engagement_rate ? Number(creator.engagement_rate) : 0;
  const engagementPercent = engagementRate.toFixed(2);

  const truncateBio = (bio: string, maxLength: number = 150) => {
    if (!bio) return '';
    if (bio.length <= maxLength) return bio;
    return bio.substring(0, maxLength) + '...';
  };

  const extractEmailFromBio = (bio: string): string | null => {
    if (!bio) return null;
    const emailRegex = /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/gi;
    const matches = bio.match(emailRegex);
    return matches ? matches[0] : null;
  };

  const displayEmail = creator.email || extractEmailFromBio(creator.bio);

  const getAgencyInfo = () => {
    if (creator.is_in_agency !== null && creator.is_in_agency !== undefined) {
      if (creator.is_in_agency === true) {
        return {
          label: creator.agency_name ? `En agence (${creator.agency_name})` : 'En agence',
          isInAgency: true,
        };
      } else {
        return {
          label: 'Libre',
          isInAgency: false,
        };
      }
    }

    return inferAgencyFromInfluencer(creator.bio, creator.username);
  };

  const agencyInfo = getAgencyInfo();
  const effectiveIsInAgency = agencyInfo.isInAgency;

  const getPriorityInfo = () => {
    if (creator.recruit_priority !== null && creator.recruit_priority !== undefined) {
      const priority = creator.recruit_priority;
      if (priority >= 4) {
        return { label: 'À contacter', level: priority };
      } else if (priority >= 2) {
        return { label: 'À voir', level: priority };
      }
      return { label: 'Faible priorité', level: priority };
    }

    return computeRecruitPriority(
      creator.followers_count || 0,
      engagementRate,
      effectiveIsInAgency
    );
  };

  const priorityInfo = getPriorityInfo();

  const getAgencyBadge = () => {
    const colorClass = effectiveIsInAgency === true
      ? 'bg-red-500/10 text-red-400 border-red-500/20'
      : effectiveIsInAgency === false
      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
      : 'bg-white/[0.06] text-white/40 border-white/[0.08]';

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        {agencyInfo.label}
      </span>
    );
  };

  const getPriorityBadge = () => {
    const colorClass = priorityInfo.level >= 4
      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
      : priorityInfo.level === 3
      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
      : 'bg-white/[0.06] text-white/40 border-white/[0.08]';

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        {priorityInfo.label}
      </span>
    );
  };

  const getRecruitmentStatusBadge = () => {
    if (!isRecruited || !recruitmentStatus) return null;

    const statusConfig: Record<string, { label: string; colorClass: string }> = {
      spotted: { label: 'Repéré', colorClass: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      contacted: { label: 'Contacté', colorClass: 'bg-white/[0.08] text-white/60 border-white/[0.12]' },
      validated: { label: 'Validé', colorClass: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
      in_agency: { label: 'En agence', colorClass: 'bg-white/[0.08] text-white/60 border-white/[0.12]' },
      refused: { label: 'Refusé', colorClass: 'bg-red-500/10 text-red-400 border-red-500/20' },
    };

    const config = statusConfig[recruitmentStatus] || statusConfig.spotted;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${config.colorClass}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 hover:bg-white/[0.06] hover:border-white/[0.14] transition-all duration-200 flex flex-col">
      <div className="flex items-start gap-3 mb-3">
        <img
          src={creator.profile_image_url || 'https://via.placeholder.com/80'}
          alt={creator.display_name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-white/[0.08]"
          onError={(e) => {
            e.currentTarget.src = 'https://via.placeholder.com/80';
          }}
        />

        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-white truncate">
            {creator.display_name || 'Sans nom'}
          </h3>
          {creator.username && (
            <p className="text-white/40 text-sm truncate">@{creator.username}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white/[0.04] rounded-lg p-2.5 text-center border border-white/[0.06]">
          <p className="text-white/40 text-[10px] uppercase tracking-wide font-semibold mb-0.5">Abonnés</p>
          <p className="text-white font-bold text-sm">
            {formatNumber(creator.followers_count || 0)}
          </p>
        </div>
        <div className="bg-white/[0.04] rounded-lg p-2.5 text-center border border-white/[0.06]">
          <p className="text-white/40 text-[10px] uppercase tracking-wide font-semibold mb-0.5">Engagem</p>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${getEngagementColor(engagementRate)}`}>
            {engagementPercent}%
          </span>
        </div>
        <div className="bg-white/[0.04] rounded-lg p-2.5 text-center border border-white/[0.06]">
          <p className="text-white/40 text-[10px] uppercase tracking-wide font-semibold mb-0.5">Moy. vues</p>
          <p className="text-white font-bold text-sm">
            {formatNumber(creator.avg_views || 0)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {getAgencyBadge()}
        {getPriorityBadge()}
        {getRecruitmentStatusBadge()}
      </div>

      {creator.bio && (
        <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-4 flex-1">
          {truncateBio(creator.bio, 120)}
        </p>
      )}

      {displayEmail && (
        <div className="mb-3 flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-lg p-2.5">
          <Mail className="h-3.5 w-3.5 text-white/40 flex-shrink-0" />
          <span className="text-xs text-white/60 truncate flex-1">{displayEmail}</span>
          <button
            onClick={copyEmail}
            className="flex-shrink-0 p-1.5 hover:bg-white/[0.08] rounded transition-colors"
            title="Copier l'email"
          >
            {emailCopied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-white/40" />
            )}
          </button>
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <button
          onClick={onGenerateMessage}
          className="flex-1 px-4 py-2.5 bg-white text-black hover:bg-white/90 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm font-semibold"
        >
          <MessageSquare className="h-4 w-4" />
          Générer message
        </button>
        {onAddToRecruitment && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddToRecruitment();
            }}
            className={`px-3 py-2.5 rounded-xl transition-all duration-300 flex items-center justify-center ${
              isRecruited
                ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30'
            }`}
            title={isRecruited ? 'Retirer du recrutement' : 'Ajouter au recrutement'}
          >
            {isRecruited ? <Check className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
          </button>
        )}
        {creator.username && (
          <a
            href={`https://www.instagram.com/${creator.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2.5 bg-white/[0.08] hover:bg-white/[0.14] text-white/50 rounded-xl flex items-center justify-center transition-colors"
            title="Voir sur Instagram"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
